'use client';

import { useEffect, useRef } from 'react';

const paymentMethods = [
  { name: 'Visa', color: '#1A1F71', textColor: '#fff', label: 'VISA' },
  { name: 'Mastercard', color: '#EB001B', label: '●● MC' },
  { name: 'American Express', color: '#007BC1', textColor: '#fff', label: 'AMEX' },
  { name: 'Discover', color: '#FF6600', textColor: '#fff', label: 'DISC.' },
  { name: 'Apple Pay', color: '#000', textColor: '#fff', label: '🍎 Pay' },
  { name: 'Google Pay', color: '#4285F4', textColor: '#fff', label: 'G Pay' },
  { name: 'Stripe', color: '#635BFF', textColor: '#fff', label: 'Stripe' },
  { name: 'PayPal', color: '#003087', textColor: '#fff', label: 'PayPal' },
];

export default function PaymentSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('is-visible'); }),
      { threshold: 0.1 }
    );
    sectionRef?.current?.querySelectorAll('.reveal-hidden')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="payment" ref={sectionRef} className="py-24 lg:py-32" style={{ background: '#fff' }}>
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-14 reveal-hidden">
          <span className="section-label">Secure Payments</span>
          <div className="section-divider mx-auto" />
          <h2
            className="font-heading font-extrabold leading-tight mb-4"
            style={{ fontSize: 'clamp(1.8rem, 3vw, 2.5rem)', color: '#0D1A3A', letterSpacing: '-0.025em' }}
          >
            Payments Processed Securely.<br />Every Time.
          </h2>
          <p className="text-base max-w-lg mx-auto" style={{ color: '#6B7280' }}>
            All transactions are encrypted and processed through Stripe and PayPal — two of the world&apos;s most trusted payment platforms.
          </p>
        </div>

        {/* Payment Methods Grid */}
        <div className="grid grid-cols-4 sm:grid-cols-4 md:grid-cols-8 gap-3 mb-10 reveal-hidden">
          {paymentMethods?.map((method) => (
            <div
              key={method?.name}
              className="payment-logo-wrap flex-col gap-1"
              title={method?.name}
            >
              <div
                className="w-10 h-7 rounded-md flex items-center justify-center text-xs font-black"
                style={{ background: method?.color, color: method?.textColor || '#fff', fontSize: '10px', letterSpacing: '0.02em' }}
              >
                {method?.label}
              </div>
              <span style={{ fontSize: '9px', color: '#9CA3AF', fontWeight: 600, textAlign: 'center' }}>
                {method?.name}
              </span>
            </div>
          ))}
        </div>

        {/* Trust badges */}
        <div className="grid sm:grid-cols-3 gap-5 reveal-hidden">
          {[
            {
              icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                  <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
                </svg>
              ),
              title: 'SSL Secured Checkout',
              desc: 'All payment pages are protected with 256-bit SSL encryption.',
              bg: '#1F3A8A',
            },
            {
              icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
              ),
              title: 'No Stored Card Data',
              desc: 'We never store your payment information. Stripe and PayPal handle all card data.',
              bg: '#0F2260',
            },
            {
              icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                  <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              ),
              title: 'PCI DSS Compliant',
              desc: 'Payments powered by Stripe and PayPal — both fully PCI DSS Level 1 certified.',
              bg: '#2D4FA3',
            },
          ]?.map((badge) => (
            <div
              key={badge?.title}
              className="p-6 rounded-2xl"
              style={{ background: '#EEF2FF', border: '1px solid rgba(31,58,138,0.12)' }}
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                style={{ background: badge?.bg }}
              >
                {badge?.icon}
              </div>
              <h4 className="font-heading font-bold mb-1.5" style={{ fontSize: '14px', color: '#0D1A3A' }}>
                {badge?.title}
              </h4>
              <p style={{ fontSize: '13px', color: '#6B7280', lineHeight: 1.6 }}>{badge?.desc}</p>
            </div>
          ))}
        </div>

        {/* PayPal email note */}
        <div
          className="mt-8 p-5 rounded-2xl flex items-center gap-4 reveal-hidden"
          style={{ background: '#F8FAFF', border: '1px solid #E5E9F5' }}
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#003087' }}>
            <span style={{ color: '#fff', fontWeight: 900, fontSize: '10px', letterSpacing: '0.02em' }}>PP</span>
          </div>
          <div>
            <p className="font-heading font-bold text-sm" style={{ color: '#0D1A3A' }}>PayPal Business Account</p>
            <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>
              PayPal payments: <span style={{ color: '#1F3A8A', fontWeight: 600 }}>contact@cartelix-llc.com</span>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}